import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTenantBySlug } from '../hooks/useNode';
import { useAnnouncements } from '../hooks/useAnnouncements';
import { AnnouncementCard } from '../components/pulse/AnnouncementCard';
import { PublicSubscribeWidget } from '../components/pulse/PublicSubscribeWidget';
import { Spinner } from '../components/ui/Spinner';
import { Megaphone, ArrowLeft, Globe, Share2, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import toast from 'react-hot-toast';
import { SEO } from '../components/SEO';

export const PublicPulsePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: node, isLoading: isNodeLoading } = useTenantBySlug(slug || '');
  
  const { data: announcements = [], isLoading: isAnnLoading } = useAnnouncements(node?.id || '', false);
  
  const publicAnnouncements = announcements.filter(ann => ann.audience === 'PUBLIC');

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (isNodeLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center dark:bg-slate-950">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!node) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 dark:bg-slate-950">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Organization not found</h1>
        <p className="text-gray-500 mb-4">The link you followed may be broken or the organization may have been moved.</p>
        <Link 
          to="/"
          className="inline-flex items-center justify-center gap-2 rounded-none uppercase font-bold tracking-widest font-mono transition-all duration-150 bg-white text-black border-2 border-black hover:bg-[#FFD600] dark:bg-black dark:text-white dark:border-white px-6 py-3 text-xs shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
        >
          Return to Homepage
        </Link>
      </div>
    );
  }

  const pulseSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${node.name} News & Updates`,
    "description": `Public announcements and updates from ${node.name}`,
    "publisher": {
      "@type": "Organization",
      "name": node.name,
      "logo": node.config?.logoUrl || undefined
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <SEO 
        title={`${node.name} | News & Updates`}
        description={`Stay updated with the latest news, announcements, and events from ${node.name}.`}
        schema={pulseSchema}
      />
      {/* Public Header */}
      <div className="bg-white border-b border-black dark:border-white dark:bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link 
              to="/" 
              className="text-gray-400 hover:text-gray-600 inline-flex items-center gap-1.5 font-mono text-xs" 
              aria-label="Back to OrgSets Homepage"
            >
              <ArrowLeft size={18} />
              <span className="sr-only">Back to OrgSets Homepage</span>
            </Link>
            <div className="h-8 w-px bg-gray-200" />
            <div className="flex items-center gap-3">
              {node?.config?.logoUrl ? (
                <img src={node.config?.logoUrl} alt={node.name} className="w-6 h-6 rounded border-2 border-black dark:border-white bg-slate-900/5 dark:bg-white/90 p-0.5" />
              ) : (
                <div className="w-6 h-6 rounded bg-brand-primary/10 flex items-center justify-center">
                  <Zap size={12} className="text-brand-primary fill-current" />
                </div>
              )}
              <span className="font-bold text-gray-900 dark:text-gray-100 tracking-tight">{node.name}</span>
            </div>
          </div>
          
          <Button variant="secondary" size="sm" onClick={handleShare}>
            <Share2 size={16} />
            Share feed
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Feed */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">News & Announcements</h1>
                <Globe size={18} className="text-blue-500" />
              </div>
              <p className="text-sm text-gray-500">Public updates from official organization representatives.</p>
            </div>

            {isAnnLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Spinner size="md" />
                <p className="mt-4 text-xs text-gray-400 font-medium tracking-wide uppercase">Fetching feed...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {publicAnnouncements.map((ann) => (
                  <AnnouncementCard key={ann.id} announcement={ann} />
                ))}

                {publicAnnouncements.length === 0 && (
                  <div className="bg-white border-2 border-black dark:border-white border-dashed border-black dark:border-white rounded-none p-20 text-center dark:bg-slate-900">
                    <div className="w-16 h-16 bg-white rounded-none flex items-center justify-center mx-auto mb-4 text-gray-300 dark:bg-slate-950">
                      <Megaphone size={32} />
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white">No public updates yet</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xs mx-auto">
                      Check back later or subscribe to get notified when {node.name} posts something new.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            <PublicSubscribeWidget nodeId={node.id} nodeName={node.name} />
            
            <div className="bg-navy-900 rounded-none p-6 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-navy-900/20">
              <h3 className="text-sm font-bold mb-2">Member Portal</h3>
              <p className="text-xs text-blue-200 mb-6 leading-relaxed">
                If you are a member of {node.name}, sign in to your dashboard to see internal discussions, private announcements, and your task list.
              </p>
              <Link 
                to="/sign-in"
                className="w-full inline-flex items-center justify-center font-mono font-bold text-xs uppercase tracking-wider py-3 px-6 bg-white text-navy-900 hover:bg-white/90 border-2 border-transparent transition-all rounded-none"
              >
                Member Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>

      <footer className="py-12 border-t border-black dark:border-white text-center">
        <p className="text-xs text-gray-400">
          Powered by <span className="font-bold text-gray-500">OrgSets</span> &bull; Multi-Chapter Membership Management Software
        </p>
      </footer>
    </div>
  );
};
